import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
  ApiAutomation,
  Flex,
  openNotificationWithIcon,
  PageContent,
  PermissionGuard,
  useFetch,
} from '@mss/mss-component-library';
import columns from './columns';
import { notification, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import Table from 'antd/es/table';
import { Api } from '../../../services/Api';
import dateFormat from 'dateformat';
import JsonDetail from 'jsx/common/JsonDetail';
import StatusCircle from 'jsx/common/StatusCircle';
import classNames from 'classnames';

const { Option } = Select;

export const CustomBreadCrumb = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  return (
    <span
      className={classNames('text-primary cursor-pointer', className)}
      {...props}
    />
  );
};

export type AutomationEventLog = {
  attributes: any;
  automation_id: string;
  entity_id: string;
  message: string;
  state: string;
};
type AutomationState = {
  date: string;
  state: string;
};

export default function AutomationEventLogs() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { automationId = '' } = useParams<{ automationId: string }>();

  const [states, setStates] = useState<AutomationState[]>([]);
  const [selectedState, setSelectedState] = useState<AutomationState>();
  const [eventLogs, setEventLogs] = useState<AutomationEventLog[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [modal, setModal] = useState<AutomationEventLog | boolean>(false);

  const [t] = useTranslation(['common', 'configuration']);

  const [{ data: automation }] = useFetch<ApiAutomation>({
    url: `/api/automation/${automationId}/`,
    defaultValue: {},
  });

  useEffect(() => {
    async function fetchStates() {
      setLoading(true);
      try {
        const fetchedStates = await Api.fetchEventLogStates(automationId);
        setStates(fetchedStates);
        setSelectedState(fetchedStates[0] || {});
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get event logs data'),
          String(e)
        );
      } finally {
        setLoading(false);
      }
    }
    fetchStates();
  }, [automationId, t]);

  useEffect(() => {
    async function fetchEventLogs() {
      setLoading(true);
      if (selectedState) {
        try {
          const fetchedLogs = await Api.fetchEventsByDate(
            automationId,
            selectedState.date
          );
          setEventLogs(fetchedLogs);
        } catch (e) {
          openNotificationWithIcon(
            notification.error,
            t('Unable to get event logs data'),
            String(e)
          );
        } finally {
          setLoading(false);
        }
      }
    }
    fetchEventLogs();
  }, [automationId, states, selectedState, t]);

  const handleDateChange = (date: string) => {
    const foundState = states.find(
      (state: AutomationState) => state.date === date
    );
    setSelectedState(foundState);
  };

  const resetDateSource = async (dropDownVisible: any) => {
    if (dropDownVisible) {
      setLoading(true);
      try {
        const fetchedStates = await Api.fetchEventLogStates(automationId);
        setStates(fetchedStates);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get event logs data'),
          String(e)
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const goToAutomationsList = () => {
    history.push(`${url.replace(`/${automationId}/log`, '')}`);
  };

  const goToAutomation = () => {
    history.push(`${url.replace('/log', '')}`);
  };

  return (
    <PermissionGuard codename={'view_automation'} redirect>
      <PageContent
        title={
          <Flex h="between" v="center" className="pb-12">
            <h4 className="m-0">
              <CustomBreadCrumb onClick={() => goToAutomationsList()}>
                {t('configuration:Automations')}
              </CustomBreadCrumb>
              <span> / </span>
              <CustomBreadCrumb onClick={() => goToAutomation()}>
                {automation?.name}
              </CustomBreadCrumb>
              <span> / Log</span>
            </h4>
            <Select
              onChange={handleDateChange}
              value={selectedState?.date || t('No data')}
              loading={loading}
              onDropdownVisibleChange={visibility =>
                resetDateSource(visibility)
              }
            >
              {states.map((state: AutomationState) => (
                <Option key={state.date} value={state.date} disabled={loading}>
                  <Flex v="center">
                    <StatusCircle
                      size={8}
                      text={state.state || ''}
                      hideText={true}
                    />
                    <span className="text-success">
                      {dateFormat(
                        new Date(state.date + 'Z') || '',
                        'mmmm dS, yyyy @ h:MM:ss TT'
                      )}
                    </span>
                  </Flex>
                </Option>
              ))}
            </Select>
          </Flex>
        }
      >
        <>
          <Table
            className="event-log-table"
            bordered={false}
            pagination={false}
            dataSource={eventLogs}
            loading={loading}
            columns={columns(t, x => setModal(x))}
          />
          <JsonDetail
            record={modal}
            closeModal={() => setModal(false)}
          ></JsonDetail>
        </>
      </PageContent>
    </PermissionGuard>
  );
}
